import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { Clock, User, Calendar, ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import { blogPosts } from "../data/blogdata";

const proseClasses = `
  text-gray-300 leading-8 text-lg
  [&_h1]:hidden
  [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-white
  [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-white
  [&_p]:mb-5
  [&_strong]:text-white
  [&_a]:text-blue-400 [&_a]:underline hover:[&_a]:text-blue-300
  [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul>li]:mb-2
  [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol>li]:mb-2
  [&_blockquote]:my-6 [&_blockquote]:rounded-xl [&_blockquote]:border [&_blockquote]:border-blue-500/30
  [&_blockquote]:bg-blue-500/10 [&_blockquote]:p-5
  [&_table]:my-8 [&_table]:w-full [&_table]:border-collapse [&_table]:text-base
  [&_th]:border [&_th]:border-white/10 [&_th]:bg-white/10 [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:text-white
  [&_td]:border [&_td]:border-white/10 [&_td]:px-4 [&_td]:py-3
`;

const formatDate = (timestamp) =>
  new Date(timestamp * 1000).toLocaleDateString("fr-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-4 text-center text-white">
        <h1 className="mb-4 text-3xl font-bold">Article introuvable</h1>
        <Link to="/blog" className="text-blue-400 hover:text-blue-300">
          ← Retour au blog
        </Link>
      </div>
    );
  }

  const cleanHtml = DOMPurify.sanitize(post.content);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: new Date(post.createdAt * 1000).toISOString(),
    author: { "@type": "Organization", name: post.author },
    publisher: { "@type": "Organization", name: "SERVICE IPTV", url: "https://servicetv.ca" },
    mainEntityOfPage: `https://servicetv.ca/blog/${post.slug}`,
  };

  return (
    <div className="min-h-screen w-full bg-slate-950 text-white">
      <Seo
        title={post.title}
        description={post.excerpt}
        url={`https://servicetv.ca/blog/${post.slug}`}
        image={post.image}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <section className="relative overflow-hidden border-b border-white/5 px-4 py-20 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/40 via-slate-950 to-slate-950" />

        <div className="relative mx-auto max-w-4xl">
          <Link
            to="/blog"
            className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-blue-400 transition-all hover:gap-3 hover:text-blue-300"
          >
            <ArrowLeft size={16} />
            Retour au blog
          </Link>

          <div className="mb-5">
            <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white">
              {post.category}
            </span>
          </div>

          <h1 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          <p className="mt-6 text-lg leading-8 text-gray-400">{post.excerpt}</p>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <User size={15} />
              {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={15} />
              {formatDate(post.createdAt)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={15} />
              {post.readingTime} min
            </span>
          </div>
        </div>
      </section>

      {post.image && (
        <div className="mx-auto max-w-4xl px-4 pt-10 sm:px-6 lg:px-8">
          <img
            src={post.image}
            alt={post.title}
            className="h-64 w-full rounded-2xl border border-white/10 object-cover sm:h-96"
          />
        </div>
      )}

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <div className={proseClasses} dangerouslySetInnerHTML={{ __html: cleanHtml }} />

        <div className="mt-16 rounded-2xl border border-white/10 bg-white/5 p-8 text-center">
          <h2 className="text-2xl font-bold">Essayez SERVICE IPTV pendant 24 heures</h2>
          <p className="mx-auto mt-3 max-w-xl text-gray-400">
            Testez le service avant de choisir un abonnement plus long.
          </p>
          <Link
            to="/pricing"
            className="mt-6 inline-block rounded-xl bg-blue-600 px-8 py-3 font-semibold text-white transition-colors hover:bg-blue-500"
          >
            Voir les prix
          </Link>
        </div>
      </article>
    </div>
  );
}

export default BlogPost;